import { useLayoutEffect, useState, RefObject, useCallback } from 'react';

interface UseDropdownPositionProps {
  dropdownRef: RefObject<HTMLElement>;
  isExpanded: boolean;
  listMaxHeight: number;
  forcePosition?: 'above' | 'below';
}

interface UseDropdownPositionReturn {
  position: 'above' | 'below';
  isReady: boolean;
}

export const useDropdownPosition = ({
  dropdownRef,
  isExpanded,
  listMaxHeight,
  forcePosition,
}: UseDropdownPositionProps): UseDropdownPositionReturn => {
  const [position, setPosition] = useState<'above' | 'below'>('below');
  const [isReady, setIsReady] = useState(false);

  const calculatePosition = useCallback(() => {
    // If position is forced, use that
    if (forcePosition) {
      setPosition(forcePosition);
      setIsReady(true);
      return;
    }

    const dropdownElement = dropdownRef.current;
    if (!dropdownElement) return;

    const rect = dropdownElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    const SPACING_BUFFER = 20;
    const requiredSpace = listMaxHeight + SPACING_BUFFER;

    if (spaceBelow >= requiredSpace) {
      setPosition('below');
    } else if (spaceAbove >= requiredSpace) {
      setPosition('above');
    } else {
      setPosition(spaceBelow >= spaceAbove ? 'below' : 'above');
    }
    setIsReady(true);
  }, [forcePosition, dropdownRef, listMaxHeight]);

  useLayoutEffect(() => {
    if (isExpanded) {
      setIsReady(false);
      calculatePosition();

      window.addEventListener('scroll', calculatePosition, true);
      window.addEventListener('resize', calculatePosition);

      return () => {
        window.removeEventListener('scroll', calculatePosition, true);
        window.removeEventListener('resize', calculatePosition);
      };
    }
  }, [isExpanded, calculatePosition]);

  return { position, isReady };
};
